import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge.tsx";
import { cn } from "@/lib/utils";

interface TagInputProps {
  value: string[];
  onChange: (newTags: string[]) => void;
  placeholder: string;
  disabled?: boolean;
  readOnly?: boolean;
  variant?: "default" | "colorful";
}

// Array of specific colors for the colorful variant
const BADGE_COLORS = [
  "#0047AB", // Deep Blue
  "#6EC6FF", // Sky Blue
  "#C4B7E6", // Lavender Gray
  "#6A49A6", // Purple Night
  "#00C4B3", // Turquoise
  "#2ABDA7", // Teal Green
  "#B2F7D2", // Mint Green
  "#A7D997", // Sage Green
  "#768A3F", // Olive Green
  "#1FAF55", // Emerald
  "#FF8686", // Salmon
  "#FFAB91", // Coral
  "#FF6347", // Sunset Orange
  "#E6C063", // Soft Gold
  "#D9A13A", // Goldenrod
  "#FFCF9F", // Peach
  "#C282D6", // Mauve
  "#B23A78", // Deep Magenta
  "#E3D5C7", // Warm Beige
  "#1589E6", // Electric Azure
  "#9C85E4", // Lavender Haze
  "#00F0DC", // Neon Aqua
  "#A0E485", // Mint Meadow
  "#8FE615", // Lime Burst
  "#FF7373", // Blazing Coral
  "#FFE373", // Golden Glow
  "#FFB273", // Sunset Peach
  "#FFAEF1", // Bubblegum Blush
  "#FF73C5", // Hot Candy
];

// Interface for tag with color
interface TagWithColor {
  text: string;
  color: string;
}

function BadgeInput({
  value = [],
  onChange,
  placeholder,
  disabled,
  readOnly,
  variant = "default",
}: TagInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [coloredTags, setColoredTags] = useState<TagWithColor[]>([]);
  const [colorIndex, setColorIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize or update coloredTags when value changes
  useEffect(() => {
    setColoredTags((prevColoredTags) => {
      // For any new tags that don't have a color assigned yet
      const updatedColoredTags = [...prevColoredTags];
      let nextColorIndex = colorIndex;

      value.forEach((tag) => {
        // Check if this tag already has a color assigned
        const existingTag = updatedColoredTags.find((t) => t.text === tag);
        if (!existingTag) {
          // Assign the next color in sequence
          const nextColor = BADGE_COLORS[nextColorIndex % BADGE_COLORS.length];
          updatedColoredTags.push({ text: tag, color: nextColor });
          nextColorIndex++;
        }
      });

      // Update the color index for the next new tag
      setColorIndex(nextColorIndex);

      // Remove any tags that are no longer in the value array
      return updatedColoredTags.filter((tag) => value.includes(tag.text));
    });
  }, [value, colorIndex]);

  const handleAddTag = (e?: React.KeyboardEvent<HTMLInputElement>) => {
    if (readOnly) return;

    if (
      (!e || e.key === "Enter" || e.key === "," || e.key === ".") &&
      inputValue.trim() !== ""
    ) {
      if (e) e.preventDefault();

      const separators = /[,.]+/;
      const newTags = inputValue
        .split(separators)
        .map((tag) => tag.trim())
        .filter((tag) => tag !== "");
      onChange([...value, ...newTags]);
      setInputValue("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = value.filter((tag) => tag !== tagToRemove);
    onChange(updatedTags);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Handle clicks on the container to focus the input
  const handleContainerClick = () => {
    const inputElement = containerRef.current?.querySelector("input");
    if (inputElement) {
      inputElement.focus();
    }
  };

  return (
    <div
      ref={containerRef}
      onClick={handleContainerClick}
      className={cn(
        "flex min-h-9 flex-wrap items-center gap-1 rounded-md border px-3 py-2 shadow-sm transition-colors",
        variant === "default"
          ? "border-input bg-transparent"
          : "border-[#333333] bg-[#111111]",
        isFocused ? "border-[#c8ff00]" : "",
      )}
    >
      {value.map((tag, index) => {
        // Find the color for this tag
        const tagWithColor = coloredTags.find((t) => t.text === tag);
        const textColor =
          variant === "colorful" && tagWithColor ? tagWithColor.color : "";

        return (
          <Badge
            key={index}
            variant={variant === "default" ? "default" : undefined}
            className={cn(
              "flex items-center gap-1",
              variant === "colorful" &&
                "rounded-full px-2 py-1 border-none bg-transparent hover:bg-[#333333] rounded-sm m-0 text-sm",
            )}
          >
            <span
              style={{ color: variant === "colorful" ? textColor : undefined }}
            >
              {tag}
            </span>
            {!readOnly && (
              <span
                className="cursor-pointer ml-1"
                style={{
                  color: variant === "colorful" ? textColor : undefined,
                }}
                onClick={() => handleRemoveTag(tag)}
              >
                ×
              </span>
            )}
          </Badge>
        );
      })}
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleAddTag}
        onBlur={() => {
          handleAddTag();
          handleBlur();
        }}
        onFocus={handleFocus}
        className={cn(
          "flex-grow outline-none bg-transparent text-sm",
          isFocused ? "border-none" : "border-input",
        )}
        placeholder={value.length === 0 ? placeholder : ""}
        disabled={disabled}
        readOnly={readOnly}
      />
    </div>
  );
}

export { BadgeInput };
